import React, { useEffect, useState } from 'react';
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import { useRecoilValue } from 'recoil';
import { resetRecoil, setRecoil } from 'recoil-nexus';
import styled from 'styled-components';
import InterphoneController from '../../controller/InterphoneController';
import {
  areaState,
  INTERPHONE_STATUS_CALLING,
  INTERPHONE_STATUS_CANCEL,
  INTERPHONE_STATUS_CONNECTING_WAIT,
  INTERPHONE_STATUS_NONE,
  INTERPHONE_STATUS_RECEIVER_WAIT,
  INTERPHONE_STATUS_SENDER_WAIT,
} from '../../recoil/area';
import { fcmInterphoneState, InterphoneType } from '../../recoil/fcmMessage';
import '../../styles/CustomModal.css';
import { CallEndAudio, CallRingtoneAudio, CallWaitAudio } from '../../utils/Audio';
import useJanusAudioRoom from '../janus/useJanusAudioRoom';
import Spinner from '../Spinner';
import { getActionData, isInAppWebView } from '../../utils/util';
import { pushButtonClickState } from '../../recoil/pushButtonClick';

let timer: any;
let waitAudioTimer: any;
let ringtoneAudioTimer: any;

type Props = {
  isVisible: boolean;
  setVisible: (bVisible: boolean) => void;
};

const InterphoneModal = ({ isVisible, setVisible }: Props) => {
  const fcmInterphoneFlag = useRecoilValue<InterphoneType>(fcmInterphoneState);
  const interphoneArea = useRecoilValue<string | null>(areaState);
  const pushButtonClickFlag = useRecoilValue<string | null>(pushButtonClickState);

  const [sendType, setSendType] = useState<string>('');
  const [seconds, setSeconds] = useState<number>(0);

  const { joined, error, leaveRoom } = useJanusAudioRoom({
    roomId: fcmInterphoneFlag.room_name,
    canJoin: fcmInterphoneFlag.room_name ? true : false,
    sendType: sendType,
  });

  const onCloseModal = () => {
    console.log('modal exit');
    setSendType('');
    setVisible(false);
  };

  // API
  const acceptVoiceChat = async () => {
    try {
      const oParams = {
        manage_id: fcmInterphoneFlag.interphone_call_manage_id,
      };
      const oRes = await InterphoneController.acceptVoiceChat(oParams);

      if (oRes && oRes.data) {
        const oResponse = oRes.data;
        console.log('acceptVoiceChat', oResponse);
        if (oResponse.data && oResponse.data.status) {
          setRecoil(fcmInterphoneState, (prev) => ({
            ...prev,
            room_name: oResponse.data.room_name,
          }));
          sendWebApp();
        }
      }
    } catch (err) {
      console.log('acceptVoiceChat err---->', err);
    }
  };

  // 0: Timeout, 1: SenderCancel, 2: ReceiverCancel
  const cancelVoiceChat = async (type: number) => {
    try {
      const oParams = {
        action:
          type === INTERPHONE_STATUS_SENDER_WAIT
            ? 'SENDER_CANCEL'
            : type === INTERPHONE_STATUS_RECEIVER_WAIT
            ? 'RECEIVER_CANCEL'
            : type === 0 && 'SENDER_TIMEOUT',
        manage_id: fcmInterphoneFlag.interphone_call_manage_id,
      };

      const response = await InterphoneController.cancelVoiceChat(oParams);

      if (response && response.data) {
        const oRes = response.data;
        console.log('cancelVoiceChat', oRes);

        if (oRes.ok && oRes.data && oRes.data.status) {
          if (type === 0) {
            resetRecoil(fcmInterphoneState);
            resetRecoil(areaState);
          }
          return true;
        }
      }
    } catch (err) {
      console.log('requestVoiceChat err-->', err);
    }
    return false;
  };

  // 통화 버튼 나가기
  const handleLeave = () => {
    if (fcmInterphoneFlag.status === INTERPHONE_STATUS_CALLING) {
      leaveRoom();
    } else if (fcmInterphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
      const isCancel = cancelVoiceChat(INTERPHONE_STATUS_SENDER_WAIT).then(() => {
        leaveRoom();
      });
      if (!isCancel) return;
    } else if (fcmInterphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT) {
      const isCancel = cancelVoiceChat(INTERPHONE_STATUS_RECEIVER_WAIT).then(() => {
        leaveRoom();
      });
      if (!isCancel) return;
    }
    sendWebApp();
    resetRecoil(fcmInterphoneState);
    resetRecoil(areaState);
  };

  const handleLeaveScreen = () => {
    console.log('========== leavePage ==============');
    leaveRoom();
    sendWebApp();
    resetRecoil(fcmInterphoneState);
    resetRecoil(areaState);
  };

  const sendWebApp = () => {
    // webView 로그인 데이터 전송
    if (isInAppWebView()) {
      window.ReactNativeWebView?.postMessage(getActionData('vibration', { vibration: false }));
    }
  };

  // 통화 상태 title
  const getInterphoneTitle = () => {
    switch (fcmInterphoneFlag.status) {
      case INTERPHONE_STATUS_NONE:
        return '통화 종료';
      case INTERPHONE_STATUS_SENDER_WAIT:
        return '전화 거는중';
      case INTERPHONE_STATUS_RECEIVER_WAIT:
        return '전화 수신중';
      case INTERPHONE_STATUS_CALLING:
        return '통화중';
      case INTERPHONE_STATUS_CONNECTING_WAIT:
        return '연결중';
      default:
        return fcmInterphoneFlag.reject_reason || '현재 통화중입니다';
    }
  };

  // 통화 시간 UI
  const getCallingTime = () => {
    const hour = Math.floor(seconds / 3600);
    const minute = Math.floor((seconds % 3600) / 60);
    const second = seconds % 60;

    return [hour, minute, second].map((item) => String(item).padStart(2, '0')).join(':');
  };

  // Audio 재생
  const handleAudioWaitPlay = () => {
    waitAudioTimer = setTimeout(() => {
      if (fcmInterphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
        CallWaitAudio.currentTime = 0;
        CallWaitAudio.play().catch((error) => {
          console.error('Error playing audio', error);
        });
      }
    }, 1200);
  };

  const handleAudioRingtonePlay = () => {
    ringtoneAudioTimer = setTimeout(() => {
      if (fcmInterphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT) {
        CallRingtoneAudio.currentTime = 0;
        CallRingtoneAudio.play().catch((error) => {
          console.error('Error playing audio', error);
        });
      }
    }, 3000);
  };

  useEffect(() => {
    if (isVisible) {
      if (fcmInterphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
        setSendType('SENDER');
      } else if (fcmInterphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT) {
        setSendType('RECEIVER');
      }
    } else {
      clearTimeout(timer);
      timer = null;
    }
  }, [isVisible, fcmInterphoneFlag.status]);

  useEffect(() => {
    if (isVisible) {
      console.log('pushButtonClick', pushButtonClickFlag);
      if (pushButtonClickFlag === 'accept') {
        acceptVoiceChat();
        resetRecoil(pushButtonClickState);
      } else if (pushButtonClickFlag === 'decline') {
        handleLeave();
        resetRecoil(pushButtonClickState);
      }
    }
  }, [isVisible, pushButtonClickFlag]);

  useEffect(() => {
    // 60초 후 타임아웃
    if (fcmInterphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
      timer = setTimeout(() => {
        if (fcmInterphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
          cancelVoiceChat(0);
        }
      }, 60 * 1000);
    } else {
      clearTimeout(timer);
      timer = null;
    }

    // 통화 시간 변경
    let interval: any = null;

    if (fcmInterphoneFlag.status === INTERPHONE_STATUS_CALLING) {
      interval = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(interval);
      interval = null;
    }

    // 전화 대기 효과음
    if (fcmInterphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
      handleAudioWaitPlay();
      CallWaitAudio.addEventListener('ended', handleAudioWaitPlay);
    } else {
      clearTimeout(waitAudioTimer);
      waitAudioTimer = null;

      CallWaitAudio.pause();
      CallWaitAudio.currentTime = 0;
    }

    // 컬러링
    if (fcmInterphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT) {
      handleAudioRingtonePlay();
      CallRingtoneAudio.addEventListener('ended', handleAudioRingtonePlay);
    } else {
      clearTimeout(ringtoneAudioTimer);
      ringtoneAudioTimer = null;

      CallRingtoneAudio.pause();
      CallRingtoneAudio.currentTime = 0;
    }

    // 통화 종료 효과음
    if (
      fcmInterphoneFlag.status === INTERPHONE_STATUS_NONE ||
      fcmInterphoneFlag.status === INTERPHONE_STATUS_CANCEL
    ) {
      CallEndAudio.currentTime = 0;
      CallEndAudio.play();

      setTimeout(() => {
        resetRecoil(fcmInterphoneState);
        resetRecoil(areaState);
      }, 2000);
    }

    return () => {
      CallWaitAudio.removeEventListener('ended', handleAudioWaitPlay);
      CallRingtoneAudio.removeEventListener('ended', handleAudioRingtonePlay);

      clearInterval(interval);
      interval = null;

      CallWaitAudio.pause();
      CallWaitAudio.currentTime = 0;

      CallEndAudio.pause();
      CallEndAudio.currentTime = 0;

      CallRingtoneAudio.pause();
      CallRingtoneAudio.currentTime = 0;

      clearTimeout(waitAudioTimer);
      waitAudioTimer = null;

      clearTimeout(ringtoneAudioTimer);
      ringtoneAudioTimer = null;

      clearTimeout(timer);
      timer = null;
    };
  }, [fcmInterphoneFlag.status]);

  useEffect(() => {
    console.log('webRTC', joined, error, sendType);
    if (!fcmInterphoneFlag.interphone_call_manage_id) {
      onCloseModal();
    }
  }, [fcmInterphoneFlag.interphone_call_manage_id]);

  useEffect(() => {
    console.log('================ RENDER =================');

    if (fcmInterphoneFlag.interphone_call_manage_id) {
      window.addEventListener('beforeunload', handleLeave);
      window.addEventListener('popstate', handleLeave);

      return () => {
        window.removeEventListener('beforeunload', handleLeave);
        window.removeEventListener('popstate', handleLeave);
      };
    }
  }, []);

  return (
    <Modal
      open={isVisible}
      onClose={onCloseModal}
      center={false}
      classNames={{
        overlay: 'interphoneModalOverlay',
        modal: 'interphoneModal',
      }}
      showCloseIcon={false}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      animationDuration={150}>
      <Style>
        <div className="modal">
          <h2>프론트</h2>
          <div className="callStatusWrap">
            <h4>{getInterphoneTitle()}</h4>
            {(fcmInterphoneFlag.status === INTERPHONE_STATUS_NONE ||
              fcmInterphoneFlag.status === INTERPHONE_STATUS_CALLING) && (
              <h4 className="callTime">{getCallingTime()}</h4>
            )}
            {(fcmInterphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT ||
              fcmInterphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT) && (
              <Spinner size={40} />
            )}
          </div>
          <div className="footerWrap">
            <div
              className={`buttonWrap ${
                fcmInterphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT && 'receive'
              }`}>
              {fcmInterphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT && (
                <button onClick={acceptVoiceChat}>
                  <img
                    className="call"
                    src={`${process.env.PUBLIC_URL}/svg/ic_phone.svg`}
                    alt="phone"
                  />
                  <p>통화</p>
                </button>
              )}
              <button onClick={handleLeave}>
                <img src={`${process.env.PUBLIC_URL}/svg/ic_phone.svg`} alt="phone" />
                <p>연결끊기</p>
              </button>
            </div>
            <img className="logo" src={`${process.env.PUBLIC_URL}/img/knockLogo.png`} alt="phone" />
            {interphoneArea && <p className="hotelName">{interphoneArea}</p>}
          </div>
        </div>
      </Style>
    </Modal>
  );
};

export default InterphoneModal;

const Style = styled.div`
  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .modal {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #000;
    padding: 8% 8% 6% 8%;
  }

  h2,
  h4,
  p {
    color: #fff;
  }

  h2 {
    font-size: 30px;
    font-weight: 700;
  }

  .callStatusWrap {
    display: flex;
    align-items: center;
    gap: 10px;

    h4 {
      font-size: 16px;
      font-weight: 500;
    }

    /* img.indicator {
      width: 44px;
      animation: rotate 3s linear infinite;
    } */
  }

  .footerWrap {
    width: fit-content;
    display: flex;
    flex-direction: column;
    align-items: center;

    .buttonWrap {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0;
      gap: 20px;
    }

    button {
      padding: 0;
      margin-bottom: -6px;
      p {
        font-size: 12px;
        font-weight: 500;
      }
      img {
        width: 60px;
        height: 60px;
        padding: 10px;
        background: #ff3030;
        border-radius: 50%;
      }
      img.call {
        background: #009712;
        rotate: -135deg;
      }
    }

    p.hotelName {
      position: absolute;
      right: 30px;
      top: 0;
      width: fit-content;
      font-size: 12px;
      line-height: 18px;
      font-weight: 400;
      color: #555;
      background: #fff;
      border-radius: 0 0 10px 10px;
      padding: 6px 20px;
    }
  }

  .logo {
    display: none;
  }

  @media (max-width: 900px) {
    .modal {
      padding: 30px;
      padding-bottom: 40px;
    }

    h2 {
      font-size: 24px;
    }

    .callStatusWrap {
      max-width: 40%;
      flex-direction: column;
      gap: 4px;

      h4 {
        text-align: center;
        font-size: 12px;
        line-height: 18px;
      }
    }

    .footerWrap {
      width: fit-content;
      display: flex;
      flex-direction: column;
      align-items: center;

      .buttonWrap {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        gap: 10px;
      }
      .buttonWrap.receive {
        margin-right: -10px;
      }

      button {
        margin-bottom: 0;
        p {
          display: none;
        }
        img {
          width: 48px;
          height: 48px;
          padding: 6px;
        }
      }

      p.hotelName {
        min-width: 70px;
        max-width: 40%;
        top: auto;
        bottom: 0;
        font-size: 10px;
        line-height: 14px;
        font-weight: 500;
        border-radius: 10px 10px 0 0;
        padding: 4px 12px;
        text-align: center;
      }
    }

    .logo {
      display: block;
      position: absolute;
      bottom: 8px;
      left: 30px;
      width: 30%;
      max-width: 150px;
    }
  }
`;
